import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Button, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { selectMiscItems } from '@/app/services/miscItems'
import { selectQuoteItems } from '@/app/services/quoteItems'
import {
    QUOTE_ADJUSTMENT_TYPE,
    quotesApi,
    useAdjustQuotePriceMutation,
    useCalculateQuoteMutation,
} from '@/app/services/quotes'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectOrganisationId } from '@/app/slices/organisationSlice'
import { setSelectedQuoteStatus } from '@/app/slices/quoteItemsSlice'
import TbxDialog from '@/common/components/TbxDialog/TbxDialog'
import { getErrorMessage } from '@/common/helpers/errorUtilities'
import { useCurrencyFormatter } from '@/common/hooks/useCurrencyFormatter'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { QUOTE_STATUS_ARRAY } from '@/common/utils/Constants/Constants'
import { getCurrencyFormat } from '@/common/utils/CurrencyUtils/CurrencyUtils'

import { summariseItemAdjustments } from './summariseItemAdjustments'

// The section sits on a white card, so MUI's light defaults do most of the work
const classes = {
    toggle: {
        color: 'text.primary',
        px: 1.5,
        py: 0.5,
        textTransform: 'none',
        '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'secondary.main',
            color: 'secondary.contrastText',
            fontWeight: 600,
        },
        // Otherwise the blue fill above wins and a disabled choice still looks clickable
        '&.Mui-selected.Mui-disabled': {
            backgroundColor: 'action.disabledBackground',
            color: 'action.disabled',
        },
    },
    // Same height as the Apply and Clear buttons
    field: {
        width: 140,
        '& .MuiInputBase-root': {
            height: 36,
        },
    },
    button: {
        height: 36,
    },
    hint: {
        color: 'text.secondary',
    },
    reason: {
        fontWeight: 600,
    },
    fixedPriceWarning: {
        color: 'warning.dark',
    },
}

const formatPercent = (percent) => `${percent > 0 ? '+' : ''}${percent}%`

/**
 * Turns an adjustment summary into the sentence shown above the controls.
 */
const describeAdjustments = ({ groups, itemCount }, t, formatCurrency) => {
    const [only] = groups
    if (!only || (groups.length === 1 && only.kind === 'none')) {
        return t('No item has an adjustment.')
    }
    if (itemCount === 1) {
        return only.kind === 'percent'
            ? t('1 item: {{percent}}', { percent: formatPercent(only.percent) })
            : t('1 item: fixed amount, {{total}}', { total: formatCurrency(only.total) })
    }
    if (groups.length === 1 && only.kind === 'percent') {
        return t('All {{count}} items: {{percent}}', { count: itemCount, percent: formatPercent(only.percent) })
    }
    if (groups.length === 1) {
        return t('All {{count}} items: fixed amounts, {{total}} in total', {
            count: itemCount,
            total: formatCurrency(only.total),
        })
    }
    const parts = groups.map((group) => {
        if (group.kind === 'percent') {
            return t('{{count}} at {{percent}}', { count: group.count, percent: formatPercent(group.percent) })
        }
        return group.kind === 'fixed'
            ? t('{{count}} with fixed amounts', { count: group.count })
            : t('{{count}} with none', { count: group.count })
    })
    return t('Different adjustments: {{list}}', { list: parts.join(', ') })
}

/**
 * The controls and the requests. Its starting values come from `summary`, so the parent
 * remounts it (by key) whenever the items' adjustments change.
 */
const AdjustmentForm = ({ disabledReason, subtotal, summary }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { quoteId } = useParams()
    const organisationId = useSelector(selectOrganisationId)
    const currencyCode = useSelector(selectCurrencyCode)
    const locale = useSelector(selectLocale)

    const { formatCurrency } = useCurrencyFormatter({ currencyCode, locale })
    const currencyFormat = getCurrencyFormat(currencyCode, locale)
    const notifyMutationError = useMutationErrorSnackbar()
    const [adjustQuotePrice, { isLoading }] = useAdjustQuotePriceMutation()
    // The shared key lets the item lists see this calculation and show the quote as calculating
    const [calculateQuote] = useCalculateQuoteMutation({ fixedCacheKey: 'shared-calculate-quote' })

    const { fixedPriceCount, groups, itemCount } = summary
    const sharedPercent = groups.length === 1 && groups[0].kind === 'percent' ? groups[0].percent : null
    const hasAdjustment = groups.some((group) => group.kind !== 'none')
    // The API skips parts with an active fixed price, so a quote of only those can't change
    const isAllFixedPrice = itemCount === 0 && fixedPriceCount > 0
    const reason =
        disabledReason ??
        (isAllFixedPrice ? t('Every item is a fixed-price part, so there is nothing to adjust.') : null)

    const [isMarkup, setIsMarkup] = useState(sharedPercent > 0)
    const [isAmount, setIsAmount] = useState(false)
    const [value, setValue] = useState(sharedPercent ? String(Math.abs(sharedPercent)) : '')
    const [isConfirmingClear, setIsConfirmingClear] = useState(false)

    const isDisabled = Boolean(reason) || isLoading
    const number = Number(value)
    const isValid = value !== '' && number > 0 && (isMarkup || number <= (isAmount ? subtotal : 100))
    const fieldLabel = isMarkup ? t('Mark up all items') : t('Discount all items')

    let hint
    if (isAmount) {
        hint = isMarkup
            ? t('Split by line price. Replaces the adjustment on every item.')
            : t('Up to {{amount}}. Split by line price. Replaces the adjustment on every item.', {
                  amount: formatCurrency(subtotal),
              })
    } else {
        hint = isMarkup
            ? t('Replaces the adjustment on every item. No upper limit.')
            : t('Replaces the adjustment on every item. Between 0 and 100.')
    }

    const submit = async (adjustment) => {
        try {
            await adjustQuotePrice({ organisationId, quoteId, ...adjustment }).unwrap()
            dispatch(setSelectedQuoteStatus(QUOTE_STATUS_ARRAY.NotCalculated))
        } catch (error) {
            const isRejected = error?.status === 400
            if (isRejected) {
                // One rejection is "the quote must be calculated" after a change elsewhere: reload so the status catches up
                dispatch(quotesApi.util.invalidateTags([{ type: 'Quotes', id: quoteId }]))
            }
            // Only 400s carry a message written for the user; a 500's text is a raw server error
            notifyMutationError(error, (isRejected && getErrorMessage(error)) || t('Failed to adjust the item prices.'))
            return
        }

        // Calculate straight away so the new prices show. The page syncs the status from the reloaded quote.
        try {
            await calculateQuote({ organisationId, quoteId }).unwrap()
        } catch (error) {
            notifyMutationError(
                error,
                t('The adjustment was saved, but the quote could not be calculated. Try Calculate again.')
            )
        }
    }

    const handleApply = () => {
        // Never send 0 from Apply: on this endpoint 0 means "clear every item adjustment"
        if (!isValid) return undefined
        return submit({
            type: isAmount ? QUOTE_ADJUSTMENT_TYPE.MonetaryValue : QUOTE_ADJUSTMENT_TYPE.Percentage,
            value: isMarkup ? number : -number,
        })
    }

    const handleClear = () => {
        setIsConfirmingClear(false)
        return submit({ type: QUOTE_ADJUSTMENT_TYPE.Percentage, value: 0 })
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
            <Typography variant="body2">{describeAdjustments(summary, t, formatCurrency)}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                    aria-label={t('Direction')}
                    disabled={isDisabled}
                    size="small"
                    value={isMarkup ? 'markup' : 'discount'}
                    exclusive
                    onChange={(_event, direction) => direction && setIsMarkup(direction === 'markup')}
                >
                    <ToggleButton
                        sx={classes.toggle}
                        value="discount"
                    >
                        {t('Discount')}
                    </ToggleButton>
                    <ToggleButton
                        sx={classes.toggle}
                        value="markup"
                    >
                        {t('Markup')}
                    </ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    aria-label={t('Type')}
                    disabled={isDisabled}
                    size="small"
                    value={isAmount ? 'amount' : 'percent'}
                    exclusive
                    onChange={(_event, type) => type && setIsAmount(type === 'amount')}
                >
                    <ToggleButton
                        sx={classes.toggle}
                        value="percent"
                    >
                        %
                    </ToggleButton>
                    <ToggleButton
                        sx={classes.toggle}
                        value="amount"
                    >
                        {currencyFormat.currency}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <NumericFormat
                    allowNegative={false}
                    customInput={TextField}
                    decimalScale={2}
                    decimalSeparator={currencyFormat.decimal || '.'}
                    disabled={isDisabled}
                    placeholder="0"
                    size="small"
                    slotProps={{
                        htmlInput: {
                            'aria-label': fieldLabel,
                            style: { fontSize: '14px', paddingBlock: 0 },
                        },
                        input: isAmount
                            ? {
                                  startAdornment: (
                                      <InputAdornment position="start">{currencyFormat.currency}</InputAdornment>
                                  ),
                              }
                            : { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                    }}
                    sx={classes.field}
                    thousandSeparator={currencyFormat.group}
                    value={value}
                    variant="outlined"
                    onValueChange={(values) => setValue(values.value)}
                />
                <Button
                    disabled={isDisabled || !isValid}
                    sx={classes.button}
                    variant="contained"
                    onClick={handleApply}
                >
                    {t('Apply')}
                </Button>
                <Button
                    color="inherit"
                    disabled={isDisabled || !hasAdjustment}
                    sx={classes.button}
                    variant="outlined"
                    onClick={() => setIsConfirmingClear(true)}
                >
                    {t('Clear')}
                </Button>
            </Box>
            <Typography
                sx={reason ? classes.reason : classes.hint}
                variant="body2"
            >
                {reason ?? hint}
            </Typography>
            {fixedPriceCount > 0 && !isAllFixedPrice ? (
                <Typography
                    sx={classes.fixedPriceWarning}
                    variant="body2"
                >
                    {isAmount
                        ? t(
                              "Fixed-price parts keep their price: {{count}} won't be adjusted, and the full amount is split across the other items.",
                              { count: fixedPriceCount }
                          )
                        : t("Fixed-price parts keep their price: {{count}} won't be adjusted.", {
                              count: fixedPriceCount,
                          })}
                </Typography>
            ) : null}
            <TbxDialog
                closeButtonText={t('Keep adjustments')}
                confirmButtonText={t('Clear all items')}
                content={t('This sets every item back to 0%, including adjustments set by hand.')}
                handleClose={() => setIsConfirmingClear(false)}
                handleConfirmClose={handleClear}
                isLoading={isLoading}
                isOpen={isConfirmingClear}
                maxWidth="xs"
                title={t('Clear all item adjustments?')}
            />
        </Box>
    )
}

AdjustmentForm.propTypes = {
    subtotal: PropTypes.number.isRequired,
    summary: PropTypes.shape({
        fixedPriceCount: PropTypes.number.isRequired,
        groups: PropTypes.arrayOf(PropTypes.object).isRequired,
        itemCount: PropTypes.number.isRequired,
    }).isRequired,
    disabledReason: PropTypes.string,
}

/**
 * Always-open section under the Price totals "Adjustment" row. It applies one discount or
 * markup, as a percentage or a total amount, to every quote item and misc item at once,
 * replacing their own adjustments. Clear sets every item back to 0% after a confirmation.
 *
 * The field starts from the items' shared percentage when they all have one, and empty
 * otherwise. What the items are set to is worked out from the item caches, because the API
 * keeps no value for the whole quote.
 *
 * @param {Object} props
 * @param {number} props.subtotal - The last calculated subtotal, the most a discount amount can be
 * @param {string|null} [props.disabledReason] - Why the quote can't be adjusted right now. When
 *   set, every control is disabled and this text replaces the hint.
 * @returns {JSX.Element}
 */
const QuoteAdjustmentEditor = ({ disabledReason = null, subtotal }) => {
    const { quoteId } = useParams()
    const organisationId = useSelector(selectOrganisationId)
    const quoteItems = useSelector((state) => selectQuoteItems(state, { organisationId, quoteId }))
    const miscItems = useSelector((state) => selectMiscItems(state, { organisationId, quoteId }))

    const summary = useMemo(() => summariseItemAdjustments(quoteItems, miscItems), [quoteItems, miscItems])
    // A new key remounts the form, so it starts again from the items whenever what they hold changes
    const formKey = JSON.stringify(summary)

    return (
        <AdjustmentForm
            disabledReason={disabledReason}
            key={formKey}
            subtotal={subtotal}
            summary={summary}
        />
    )
}

QuoteAdjustmentEditor.propTypes = {
    subtotal: PropTypes.number.isRequired,
    disabledReason: PropTypes.string,
}

export default QuoteAdjustmentEditor
